import { useEffect, useState } from 'react';
import { InsuranceType } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/shared/enums.pb';
import { InsuranceReportRequestMetaDataType } from 'libs/services/api-insurance-eligibility-report/src/types';
import { InsuranceDetailQueries } from '@frontend/api-insurance-detail';
import { InsuranceReportQueries } from '@frontend/api-insurance-eligibility-report';
import { NO_COVERAGE, INITIAL_RESPONSE, OUT_OF_NETWORK } from '../constants';
import {
  titleCaseWrapper,
  convertJsonToString,
  isArrayOfStrings,
  convertServiceDatesIntoServiceHistory,
} from '../helpers';
import {
  Benefit,
  PatientResponse,
  Plan,
  LimitationTableDataType,
  CoinsuranceTableDataType,
  CopaymentTableDataType,
  PlanPeriodValues,
  SummaryDataType,
  DeductibleTableDataType,
  InsuranceReportContextState,
  Coverage,
} from '../types';
import {
  getCoinsurancesTableColConfigs,
  copaymentsTableColConfigs,
  getDeductiblesTableColConfigs,
  getLimitationsTableColConfigs,
  coInsuranceTableDefaultColumnKeys,
} from './table-configs';

export const getCoinsuranceTableData = (coinsuranceData: Benefit[]) => {
  const networkDifferentiators: string[] = [];
  const coinsuranceSummary: SummaryDataType[] = [];

  const coinsuranceTableData = coinsuranceData.reduce<CoinsuranceTableDataType[]>((acc, i) => {
    const network = i.payer_specific_description ?? i?.network;
    const category = i?.procedure_code || i?.service_type || i?.category;
    if (!!network && !!category) {
      i?.network &&
        !networkDifferentiators.includes(titleCaseWrapper(i?.network)) &&
        networkDifferentiators.push(titleCaseWrapper(i?.network));

      i?.percent &&
        coinsuranceSummary.push({
          title: titleCaseWrapper(category),
          subTitle: titleCaseWrapper(i?.network ?? '' + i?.payer_specific_description ?? ''),
          labels: ['percent'],
          percent: Number(i.percent),
          successor: '%',
          metadata: {
            network: i?.network ? titleCaseWrapper(i?.network) : '',
            coverageLevel: i?.coverage_level ? titleCaseWrapper(i?.coverage_level) : '',
          },
        });
      acc.push({
        network: titleCaseWrapper(network),
        category: titleCaseWrapper(category),
        percent: Number(i.percent) || Number(0),
        planPeriod: titleCaseWrapper(i.plan_period),
        disclaimers: titleCaseWrapper(Array.isArray(i?.disclaimers) ? i?.disclaimers.join(', ') : i?.disclaimers),
        message: titleCaseWrapper(i?.message),
        serviceType: titleCaseWrapper(i?.service_type || '-'),
        coverageLevel: titleCaseWrapper(i?.coverage_level || '-'),
        insuranceType: titleCaseWrapper(i?.insurance_type || '-'),
        authorizationRequired: titleCaseWrapper(i?.authorization_required || '-'),
      });
    }
    return acc;
  }, []);

  const coInsuranceTableColumnKeys = getFilteredKeysForCoInsuranceTable(
    coinsuranceTableData,
    coInsuranceTableDefaultColumnKeys
  );

  const coinsurancesTableColConfigs = getCoinsurancesTableColConfigs(coInsuranceTableColumnKeys);

  return {
    coinsuranceTableData,
    coinsurancesTableColConfigs,
    coinsuranceSummary: coinsuranceSummary.sort((a, b) => (a?.percent && b?.percent ? b.percent - a.percent : 0)),
    shouldShowCoinsurance: coinsuranceTableData && coinsuranceTableData.length > 0,
    networkDifferentiators,
  };
};

const getFilteredKeysForCoInsuranceTable = (
  dataArray: CoinsuranceTableDataType[],
  keys: (keyof CoinsuranceTableDataType)[]
): (keyof CoinsuranceTableDataType)[] => {
  return keys.filter((key) => dataArray.some((data) => data[key] && data[key] !== '-'));
};

export const getCoPaymentsTableData = (coPaymentsData: Benefit[]) => {
  const copaymentSummary: SummaryDataType[] = [];
  const networkDifferentiators: string[] = [];
  const coverageDifferentiator: string[] = [];

  const coPaymentTableData = coPaymentsData.reduce<CopaymentTableDataType[]>((acc, i) => {
    const network = i.payer_specific_description ?? i.network;
    const category = i?.procedure_code || i?.service_type || i?.category;

    if (!!network && !!category) {
      i?.network &&
        !networkDifferentiators.includes(titleCaseWrapper(i?.network)) &&
        networkDifferentiators.push(titleCaseWrapper(i?.network));

      i?.coverage_level &&
        !coverageDifferentiator.includes(titleCaseWrapper(i?.coverage_level)) &&
        coverageDifferentiator.push(titleCaseWrapper(i?.coverage_level));

      i?.amount &&
        copaymentSummary.push({
          title: titleCaseWrapper(category),
          subTitle: titleCaseWrapper(i?.network ?? '' + i?.payer_specific_description ?? '' + i?.coverage_level ?? ''),
          labels: ['calendar'],
          calendar: Number(i.amount),
          metadata: {
            network: i?.network,
            coverageLevel: i?.coverage_level,
          },
        });

      acc.push({
        network: titleCaseWrapper(network),
        category: titleCaseWrapper(category),
        amount: Number(i.amount) || Number(0),
        coverageLevel: titleCaseWrapper(i.coverage_level) || '-',
        disclaimers: titleCaseWrapper(Array.isArray(i?.disclaimers) ? i?.disclaimers.join(', ') : i?.disclaimers),
      });
    }
    return acc;
  }, []);

  const shouldShowCoPayment: boolean = coPaymentTableData && coPaymentTableData.length > 0;

  return {
    coPaymentTableData,
    copaymentsTableColConfigs,
    copaymentSummary: copaymentSummary.sort((a, b) => (a?.calendar && b?.calendar ? b.calendar - a.calendar : 0)),
    shouldShowCoPayment,
    networkDifferentiators,
    coverageDifferentiator,
  };
};

export const getDeductibleTableData = (deductibleData: Benefit[]) => {
  const deductibleSummary: SummaryDataType[] = [];
  const coverageSet: string[] = [];
  const networkDifferentiators: string[] = [];
  const coverageDifferentiator: string[] = [];

  const obj: {
    [key: string]: {
      // network level
      [key: string]: {
        // coverage level
        // @ts-ignore
        none?: {
          planPeriod?: string;
          calendarAmount: number;
          remainingAmount?: number;
          coverageLevel?: string;
          disclaimers?: string;
          network?: string;
          insuranceType?: string;
          message?: string;
        }[];
        [key: string]: {
          planPeriod?: string;
          calendarAmount: number;
          remainingAmount?: number;
          coverageLevel?: string;
          disclaimers: string;
          network?: string;
          insuranceType?: string;
          message?: string;
        };
      };
    };
  } = {};

  deductibleData.forEach((i) => {
    const networkRef = i?.payer_specific_description ?? i?.network;
    const category = i.procedure_code || i.service_type || i.category || i.code || i.coverage_level;
    if (networkRef && category) {
      if (!Object.keys(obj).includes(networkRef)) {
        obj[networkRef] = {};
      }

      i?.network &&
        !networkDifferentiators.includes(titleCaseWrapper(i?.network)) &&
        networkDifferentiators.push(titleCaseWrapper(i?.network));
      i?.coverage_level &&
        !coverageDifferentiator.includes(titleCaseWrapper(i?.coverage_level)) &&
        coverageDifferentiator.push(titleCaseWrapper(i?.coverage_level));

      const dataToPush: {
        planPeriod?: string;
        calendarAmount: number;
        remainingAmount?: number;
        coverageLevel?: string;
        disclaimers: string;
        insuranceType?: string;
        message?: string;
      } = {
        planPeriod: i.plan_period,
        calendarAmount: Number(i.amount),
        remainingAmount: Number(i.amount),
        coverageLevel: i.coverage_level,
        disclaimers: titleCaseWrapper(Array.isArray(i?.disclaimers) ? i?.disclaimers?.join(', ') : i?.disclaimers),
        insuranceType: titleCaseWrapper(i?.insurance_type || '-'),
        message: titleCaseWrapper(i?.message || '-'),
      };

      if (!obj[networkRef][category]) {
        obj[networkRef][category] = {};
      }

      if (!i.coverage_level || !i.plan_period) {
        if (!!obj?.[networkRef]?.[category]?.['none'] && Array.isArray(obj[networkRef][category]['-'])) {
          obj?.[networkRef]?.[category]?.['none']?.push(dataToPush);
        } else {
          obj[networkRef][category]['none'] = [dataToPush];
        }
      } else {
        const refObject = obj[networkRef][category][i.coverage_level];
        if (!refObject) {
          obj[networkRef][category][i.coverage_level] = {
            ...obj[networkRef][category][i.coverage_level],
          };
        }

        const isCalendarOrServicePlanPeriod =
          i.plan_period?.toLowerCase()?.includes('calendar') || i.plan_period?.toLowerCase()?.includes('service');

        const isRemainingPlanPeriod = i.plan_period?.toLowerCase()?.includes('remaining');

        if (isCalendarOrServicePlanPeriod) {
          obj[networkRef][category][i.coverage_level] = {
            ...obj[networkRef][category][i.coverage_level],
            calendarAmount: Number(i.amount),
            disclaimers: Array.isArray(i.disclaimers) ? i.disclaimers.join(', ') : i?.disclaimers || '-',
            network: i?.network,
          };
        } else if (isRemainingPlanPeriod) {
          obj[networkRef][category][i.coverage_level] = {
            ...obj[networkRef][category][i.coverage_level],
            remainingAmount: Number(i.amount),
          };
        } else {
          obj[networkRef][category]['none'] = [dataToPush];
        }
      }
    }
  });

  const deductibleTableData: DeductibleTableDataType[] = [];

  Object.keys(obj).forEach((network) => {
    // This tells us the network
    Object.keys(obj[network]).forEach((category) => {
      // This tells us the serviceType or procedureCode
      Object.keys(obj[network][category]).forEach((coverage) => {
        // this tells us about the coverageLevel
        const objectRef = obj[network][category][coverage];

        if (coverage === 'none' && Array.isArray(objectRef)) {
          !coverageDifferentiator.includes(NO_COVERAGE) && coverageDifferentiator.push(NO_COVERAGE);
          objectRef?.forEach((l) => {
            deductibleTableData.push({
              network: titleCaseWrapper(network),
              category: titleCaseWrapper(category),
              calendarAmount: l.calendarAmount || 0,
              remainingAmount: l.remainingAmount || 0,
              coverageLevel: titleCaseWrapper(l.coverageLevel) || '-',
              disclaimers: titleCaseWrapper(l.disclaimers),
              insuranceType: titleCaseWrapper(l.insuranceType),
              message: titleCaseWrapper(l.message),
            });
          });
        } else {
          coverage && !coverageSet.includes(coverage) && coverageSet.push(titleCaseWrapper(coverage));
          objectRef?.calendarAmount &&
            deductibleSummary.push({
              title: category,
              subTitle: `${titleCaseWrapper(network)}, ${titleCaseWrapper(coverage)}`,
              labels: ['total', 'remaining', 'paid'],
              total: Number(objectRef?.calendarAmount) || 0,
              remaining: Number(objectRef?.remainingAmount) || 0,
              paid: (objectRef?.calendarAmount || 0) - Number(objectRef?.remainingAmount || 0),
              predecessor: '$',
              metadata: {
                network: objectRef?.network ? titleCaseWrapper(objectRef?.network) : '',
                coverageLevel: coverage ? titleCaseWrapper(coverage) : '',
              },
            });
          deductibleTableData.push({
            network: titleCaseWrapper(network),
            category: titleCaseWrapper(category),
            calendarAmount: objectRef.calendarAmount || 0,
            remainingAmount: objectRef.remainingAmount || 0,
            coverageLevel: titleCaseWrapper(coverage),
            disclaimers: objectRef.disclaimers,
            insuranceType: objectRef.insuranceType || '',
            message: objectRef.message || '',
          });
        }
      });
    });
  });

  return {
    deductibleTableData,
    deductiblesTableColConfigs: getDeductiblesTableColConfigs(),
    deductibleSummary: deductibleSummary.sort((a, b) => (a?.total && b?.total ? b.total - a.total : 0)),
    shouldShowDeductible: deductibleTableData && deductibleTableData.length > 0,
    networkDifferentiators,
    coverageDifferentiator,
  };
};

export const getLimitationsData = (limitationsData: Benefit[]) => {
  const limitationsTableData: LimitationTableDataType[] = [];
  limitationsData.forEach((i) => {
    const serviceDates = isArrayOfStrings(i.service_dates || [])
      ? convertServiceDatesIntoServiceHistory((i.service_dates || []) as string[], i?.procedure_code || '')
      : i.service_dates;

    const ageLimit =
      typeof i?.age_limit === 'string' ? (i.age_limit as string) : convertJsonToString(i?.age_limit || []) || '';

    limitationsTableData.push({
      coverage: titleCaseWrapper(i.service_type || i.procedure_code),
      coverageCategory: titleCaseWrapper(i.category || i?.payer_specific_description || i?.network),
      ...(i.services_remaining && {
        remaining: titleCaseWrapper(i?.services_remaining?.toString()),
      }),
      ...(i.plan_period && { planPeriod: titleCaseWrapper(i.plan_period) }),
      ...(i.coverage_level && {
        coverageLevel: titleCaseWrapper(i.coverage_level),
      }),
      ...(i.age_limit && {
        ageLimit: titleCaseWrapper(ageLimit),
      }),
      limitation: titleCaseWrapper(i?.limitation),
      disclaimers: Array.isArray(i.disclaimers) ? i.disclaimers.join(', ') : i?.disclaimers || '-',
      ...(i.limitation_applies_to && {
        limitationAppliesTo: Array.isArray(i.limitation_applies_to)
          ? i.limitation_applies_to.join(', ')
          : titleCaseWrapper(i?.limitation_applies_to),
      }),
      ...(i.identified_procedure_codes && {
        limitationAppliesTo: Array.isArray(i.identified_procedure_codes)
          ? i.identified_procedure_codes?.join(', ')
          : titleCaseWrapper(i?.identified_procedure_codes),
      }),
      serviceDates: serviceDates ?? [],
      insuranceType: titleCaseWrapper(i?.insurance_type),
      message: titleCaseWrapper(i?.message),
      procedureCode: titleCaseWrapper(i?.procedure_code),
    });
  });

  return {
    limitationsTableData,
    limitationsTableColConfigs: getLimitationsTableColConfigs(),
  };
};

export const getMaximumsData = (maximumsData: Benefit[]) => {
  const maximumsSummary: SummaryDataType[] = [];
  const obj: Record<string, Benefit[]> = {};

  maximumsData.forEach((i) => {
    const networkRef = i?.payer_specific_description ?? i?.network;
    const category = i?.service_type || i?.category || i?.procedure_code;
    if ((networkRef || category) && i.plan_period && i.amount) {
      const key = [networkRef, category, i.coverage_level].filter((item) => !!item).join('-');
      if (!obj[key]) {
        obj[key] = [];
      }
      obj[key].push(i);
    }
  });

  Object.keys(obj).forEach((key) => {
    const objectRef = obj[key];
    if (Array.isArray(objectRef) && objectRef.length > 0) {
      const summary: SummaryDataType = {
        title: titleCaseWrapper(objectRef[0].category || objectRef[0].service_type || objectRef[0].procedure_code),
        subTitle: titleCaseWrapper(objectRef[0]?.network || '' + objectRef[0]?.coverage_level || ''),
        labels: [],
        predecessor: '$',
        metadata: {
          network: objectRef[0]?.network ? titleCaseWrapper(objectRef[0]?.network) : '',
          coverageLevel: objectRef[0].coverage_level ? titleCaseWrapper(objectRef[0].coverage_level) : '',
        },
      };
      const remaining = objectRef.find((r) => r['plan_period']?.toLowerCase() === 'remaining');
      const used = objectRef.find((r) => r['plan_period'] === 'year_to_date');
      const total = objectRef.find((r) => r['plan_period'] === 'calendar');

      if (remaining?.amount && (used?.amount || total?.amount)) {
        summary['remaining'] = Number(remaining.amount);
        summary['calendar'] = Number(total?.amount ? total?.amount : used?.amount + remaining?.amount);
        summary['labels'] = ['calendar', 'remaining'];
        maximumsSummary.push(summary);
      } else {
        const planPeriods = objectRef.reduce<{ plan_period: PlanPeriodValues; amount: number }[]>((acc, r) => {
          r?.plan_period &&
            !acc.some((p) => p.plan_period == r.plan_period) &&
            acc.push({ plan_period: r.plan_period, amount: Number(r.amount) });
          return acc;
        }, []);
        if (planPeriods.length > 0) {
          summary['labels'] = planPeriods.map<PlanPeriodValues>(
            (p: { plan_period: PlanPeriodValues }) => p.plan_period
          );
          planPeriods.forEach((p) => {
            summary[p.plan_period] = p.amount;
          });
          maximumsSummary.push(summary);
        }
      }
    }
  });

  return {
    maximumsSummary,
    shouldShowMaximums: maximumsSummary.length > 0,
  };
};

export const getPatientDetails = (
  data?: PatientResponse
): {
  addressLine1?: string;
  addressLine2?: string;
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  state?: string;
  coverage?: Coverage;
} => {
  return {
    ...(!!data?.address?.street1 && {
      addressLine1: data?.address?.street1 || '',
    }),
    ...(!!data?.address?.city && {
      city: `${data?.address?.city || ''}`,
    }),
    ...(!!data?.address?.zip && {
      zipCode: data?.address?.zip || '',
    }),

    ...(!!data?.first_name && { firstName: `${data?.first_name} ${data?.middle_name ?? ''}`.trim() }),
    ...(!!data?.last_name && { lastName: data?.last_name }),
    ...(!!data?.dob && { dateOfBirth: data?.dob }),
    ...(!!data?.address?.state && { state: data?.address?.state.trim() || '' }),
    ...(!!data?.coverage && { coverage: data?.coverage }),
    ...(!!data?.subscriber_id && { subscriberId: data?.subscriber_id || '' }),
    ...(!!data?.relationship && { relationship: data?.relationship || '' }),
  };
};

export const getPlanDetails = (
  data?: Plan
): {
  planName?: string;
  groupNumber?: string;
  memberId?: string;
  type?: string;
} => {
  return {
    ...(data?.name && { planName: data.name }),
    ...(data?.group_number && { groupNumber: data.group_number }),
    ...(data?.type && { type: data.type }),
  };
};

export const useModifiedDentalCoverageResponse = (
  personId: string,
  locationId: string,
  onError?: () => void
): InsuranceReportContextState => {
  const [modifiedResponse, setModifiedResponse] = useState(INITIAL_RESPONSE);
  const { data: DentalCoverageResponse, isError: DentalCoverageError } =
    InsuranceDetailQueries.useGetCachedDentalCoverageInfoByPersonId(
      personId,
      locationId,
      InsuranceType.INSURANCE_TYPE_PRIMARY
    );

  const { data: personImageData, isSuccess: isPersonImageDataSuccess } =
    InsuranceReportQueries.usePersonImage(personId);

  useEffect(() => {
    if (DentalCoverageError) onError?.();
    else {
      const networkFilter: string[] = [];
      const coverageFilter: string[] = [];

      let response: InsuranceReportContextState = {
        ...modifiedResponse,
        isLoading: false,
        ...(DentalCoverageResponse?.lastVerifiedAt && { lastRetrievalDate: DentalCoverageResponse?.lastVerifiedAt }),
        ...(DentalCoverageResponse?.details?.limitations && {
          ...getLimitationsData(DentalCoverageResponse?.details?.limitations),
        }),
        ...(DentalCoverageResponse?.details?.maximums && {
          ...getMaximumsData(DentalCoverageResponse?.details?.maximums),
        }),
      };

      if (DentalCoverageResponse?.details?.coinsurance) {
        const { networkDifferentiators, ...rest } = getCoinsuranceTableData(
          DentalCoverageResponse?.details?.coinsurance
        );
        networkFilter.push(...networkDifferentiators);

        response = { ...response, ...rest };
      }
      if (DentalCoverageResponse?.details?.copayment) {
        const { networkDifferentiators, coverageDifferentiator, ...rest } = getCoPaymentsTableData(
          DentalCoverageResponse?.details?.copayment
        );
        networkFilter.push(...networkDifferentiators);
        coverageFilter.push(...coverageDifferentiator);

        response = { ...response, ...rest };
      }
      if (DentalCoverageResponse?.details?.deductible) {
        const { networkDifferentiators, coverageDifferentiator, ...rest } = getDeductibleTableData(
          DentalCoverageResponse?.details?.deductible
        );
        networkFilter.push(...networkDifferentiators);
        coverageFilter.push(...coverageDifferentiator);

        response = { ...response, ...rest };
      }
      response['profileData'] = {
        ...(DentalCoverageResponse?.details?.patient && {
          ...getPatientDetails(DentalCoverageResponse?.details?.patient),
        }),
        ...(DentalCoverageResponse?.details?.plan && { ...getPlanDetails(DentalCoverageResponse?.details?.plan) }),
        ...(DentalCoverageResponse?.details?.payer && {
          payerName: DentalCoverageResponse?.details?.payer?.name || '-',
          payerId: DentalCoverageResponse?.details?.payer?.id || '-',
        }),
      };

      response['subscriberData'] = {
        ...(DentalCoverageResponse?.details?.subscriber && {
          ...getPatientDetails(DentalCoverageResponse?.details?.subscriber),
        }),
        ...(DentalCoverageResponse?.details?.plan && { ...getPlanDetails(DentalCoverageResponse?.details?.plan) }),
        ...(DentalCoverageResponse?.details?.payer && {
          payerName: DentalCoverageResponse?.details?.payer?.name || '-',
          payerId: DentalCoverageResponse?.details?.payer?.id || '-',
        }),
      };

      response['showNetworkFilter'] = networkFilter.includes(OUT_OF_NETWORK) && networkFilter.length > 1;
      response['filters'] = { coverage: [], inNetworkSelected: true };

      if (coverageFilter.length > 0) {
        response['coverageTypes'] = [...new Set(coverageFilter)];
      }

      if (DentalCoverageResponse?.details?.plan) {
        response['plan'] = DentalCoverageResponse?.details?.plan;
      }

      if (DentalCoverageResponse?.insuranceRequestDetails) {
        response['insuranceVerificationRequestMetaData'] =
          DentalCoverageResponse?.insuranceRequestDetails as InsuranceReportRequestMetaDataType;
      }

      setModifiedResponse({ ...modifiedResponse, ...response });
    }
  }, [DentalCoverageResponse, DentalCoverageError]);

  useEffect(() => {
    setModifiedResponse((response) => ({ ...response, personImage: personImageData || '' }));
  }, [personImageData, isPersonImageDataSuccess]);

  return modifiedResponse;
};
